/**
 * External EOI — shared field catalogue and configuration models.
 *
 * These models are shared between the in-app configuration builder
 * (ExternalEoiComponent) and the public buyer-facing form
 * (ExternalEoiFormComponent). Keep them framework-free so they can be
 * imported by a standalone public component without pulling in the
 * project-edit module.
 */

/**
 * `localStorage` key the builder's "Preview" button writes an unsaved config to before opening
 * `EXTERNAL_EOI_PREVIEW_ROUTE` in a new tab. `localStorage` rather than `sessionStorage` — and left
 * in place rather than cleared after read — so the preview route still has something to render on
 * a refresh, a manually-typed/pasted URL, or a reopened tab, none of which carry `window.open`'s
 * session lineage back to the tab that wrote it.
 */
export const EXTERNAL_EOI_PREVIEW_STORAGE_KEY = 'external_eoi_preview_config';

/** Route the builder's "Preview" button opens in a new tab. Matched before `external-eoi/:token`. */
export const EXTERNAL_EOI_PREVIEW_ROUTE = '/external-eoi/preview';

/**
 * The kind of control rendered on the buyer-facing form.
 *
 * `info` is read-only display bound to real data (e.g. Project Name / Lot Number). `static_text`
 * is unrelated to any data — a block of copy the agent writes themselves (a disclaimer, extra
 * instructions, anything not tied to a field) — see `content` on `ExternalEoiConfigField`.
 * `checkbox`/`radio` both use `options` (like `select`) — `checkbox`'s answer is a `string[]`
 * (multi-select), `radio`'s is a single `string`. `divider` is a zero-answer visual rule; it also
 * doubles as the placeholder member of an otherwise-empty custom section (see
 * `createExternalEoiCustomSectionKey`) so an agent who creates a section and adds nothing else to
 * it does not get an empty heading on the buyer form. `columns` is a layout container — see
 * `columns`/`columnCount` on `ExternalEoiConfigField`.
 */
export type ExternalEoiFieldType =
  | 'text'
  | 'email'
  | 'tel'
  | 'number'
  | 'textarea'
  | 'select'
  | 'date'
  | 'address'
  | 'file'
  | 'info'
  | 'static_text'
  | 'checkbox'
  | 'radio'
  | 'divider'
  | 'columns';

/** How much of a section's row a field takes up on the buyer form. */
export type ExternalEoiFieldWidth = 'full' | 'half' | 'third';

/** Options offered in the Field Settings "Width" control. */
export const EXTERNAL_EOI_WIDTH_OPTIONS: { value: ExternalEoiFieldWidth; label: string }[] = [
  { value: 'full', label: 'Full Width' },
  { value: 'half', label: 'Half' },
  { value: 'third', label: 'One Third' },
];

/**
 * A field's effective width, defaulting sensibly for fields saved before `width` existed:
 * read-only info and free-text blocks default to full width (unchanged from before this was
 * configurable), everything else defaults to half — the 2-up layout the buyer form already used.
 */
export function resolveExternalEoiFieldWidth(
  field: { type: ExternalEoiFieldType; width?: ExternalEoiFieldWidth } | null | undefined
): ExternalEoiFieldWidth {
  if (field?.width) return field.width;
  if (field?.type === 'info' || field?.type === 'static_text' || field?.type === 'columns' || field?.type === 'divider') return 'full';
  return 'half';
}

/**
 * Logical grouping used to render sections on the buyer form.
 *
 * The last five mirror the tabs on the EOI record itself (Critical Dates, Sale
 * Details, Solicitor, Agency, plus an internal/admin group), so every field on
 * an EOI can be put on an external form and written straight back.
 *
 * This fixed set only ever types a *catalogue* field's section
 * (`ExternalEoiCatalogField.section`, `EXTERNAL_EOI_SECTIONS`, `EXTERNAL_EOI_PICKER_GROUPS`) — a
 * *placed* field's section (`ExternalEoiConfigField.section`) is a plain `string`, because an agent
 * can mint an arbitrary custom section (see `createExternalEoiCustomSectionKey`) alongside these ten.
 */
export type ExternalEoiSection = 'property' | 'buyer' | 'financial' | 'documents' | 'other' | 'dates' | 'sale' | 'solicitor' | 'agency' | 'internal';

/**
 * Where a field's value is prefilled from when a link is generated.
 * The backend resolves these against the linked project/lot/contact.
 */
export type ExternalEoiPrefillSource =
  | 'none'
  | 'project.name'
  | 'project.location'
  | 'lot.number'
  | 'lot.price'
  | 'lot.bed'
  | 'lot.bath'
  | 'lot.car'
  | 'lot.type'
  | 'lot.level'
  | 'lot.aspect'
  | 'lot.internal_area'
  | 'lot.external_area'
  | 'lot.total_area'
  | 'lot.internal_price_m2'
  | 'lot.storage'
  | 'lot.study'
  | 'lot.mpr'
  | 'lot.orientation'
  | 'lot.car_park_price'
  | 'lot.storage_price'
  | 'lot.total_price'
  | 'lot.status'
  | 'contact.full_name'
  | 'contact.first_name'
  | 'contact.last_name'
  | 'contact.email'
  | 'contact.mobile'
  | 'contact.phone'
  | 'contact.address'
  | 'agent.name'
  | 'agent.email';

/** A single field definition in the master catalogue. */
export interface ExternalEoiCatalogField {
  /** Stable key persisted in config and posted back on submit. */
  key: string;
  /** Human label shown by default (the agent may override it). */
  label: string;
  /** Font Awesome icon class shown in the picker/builder. */
  icon: string;
  type: ExternalEoiFieldType;
  section: ExternalEoiSection;
  /** Maps to the EOI backend field this value should populate. */
  eoiField?: string;
  /** Options for `select` type fields. */
  options?: string[];
  /** Default prefill source suggested for this field. */
  defaultPrefill?: ExternalEoiPrefillSource;
  /** Property-info fields default to read-only on the buyer form. */
  readOnlyByDefault?: boolean;
  /** Accepted extensions / max size for `file` fields. */
  fileConfig?: { accept: string; maxSizeMb: number };
  /** Fields that are almost always required in a valid EOI. */
  recommendedRequired?: boolean;
  /** Seed help text shown under the field on the buyer form (agent can edit it). */
  helpTextByDefault?: string;
}

/** A field the agent has placed on the form, with its per-project settings. */
export interface ExternalEoiConfigField {
  key: string;
  /** Overridable label shown to the buyer. */
  label: string;
  type: ExternalEoiFieldType;
  /** One of the 10 catalogue sections, or a minted custom-section key — see `ExternalEoiSection`. */
  section: string;
  eoiField?: string;
  required: boolean;
  /** Read-only prefilled info (buyer cannot change). */
  readOnly: boolean;
  /** Whether a prefilled value may be edited by the buyer. */
  allowEditing: boolean;
  prefill: ExternalEoiPrefillSource;
  helpText?: string;
  placeholder?: string;
  options?: string[];
  fileConfig?: { accept: string; maxSizeMb: number };
  /** 0-based display order within the form. */
  order: number;
  /** The copy shown for a `type: 'static_text'` block — unused by every other field type. */
  content?: string;
  /** How much of the row this field takes up. Unset defaults per `resolveExternalEoiFieldWidth`. */
  width?: ExternalEoiFieldWidth;
  /** Whether the field's label renders above its control on the buyer form. Unset/`true` = shown; only `false` hides it. */
  showLabel?: boolean;
  /**
   * True for a field minted by `createExternalEoiCustomField` — not in `EXTERNAL_EOI_FIELD_CATALOG`,
   * no `eoiField` mapping. Drives the "not linked to your CRM" notice in Field Settings: its answer
   * is only ever captured in a submission's `values`, never `eoi_values` (see
   * `buildExternalEoiSubmission`) — see the round8 backend doc for the open question of whether
   * `values` is actually persisted/visible anywhere today.
   */
  isCustom?: boolean;
  /**
   * Only present when `type === 'columns'`. Outer array = side-by-side slots, inner array = fields
   * stacked within that slot. A `columns` field is itself a normal entry in `fields` (so it keeps its
   * own `section`/`order` like any other field) — its children are NOT also top-level `fields`
   * entries; they live only here. Nesting is capped at depth 1 — a slot may never contain another
   * `columns` field. Use `flattenExternalEoiAnswerableFields` to walk every real (non-container) field
   * including these nested ones.
   */
  columns?: ExternalEoiConfigField[][];
  /** Number of slots in a `columns` field (2–4). Kept in sync with `columns.length`. */
  columnCount?: number;
}

/** The saved configuration for one project. */
export interface ExternalEoiConfig {
  project_id: string;
  /** Optional heading/intro shown above the buyer form. */
  form_title?: string;
  form_intro?: string;
  fields: ExternalEoiConfigField[];
  /**
   * Per-project overrides for the section headings the buyer form renders (`EXTERNAL_EOI_SECTIONS`'
   * defaults, e.g. "Property Details" / "Your Details"), keyed by section key. Only sections the
   * agent has actually renamed need an entry — anything missing falls back to the catalogue default.
   */
  section_labels?: Record<string, string>;
  /** Link defaults applied when generating new links. */
  default_expiry_days?: number;
  /** Lock the form once submitted (unless reopened). */
  lock_after_submit?: boolean;
  /**
   * The office SMTP account (`user_smtps._id`) the buyer's access passcode is
   * sent from.
   *
   * Null means nothing has been chosen, and the backend then falls back to the
   * default mailer — which in dev is Mailtrap, so the passcode never reaches a
   * real buyer. An office can hold several accounts with no "default" flag to
   * pick by, which is why this is a per-project choice rather than a lookup.
   */
  otp_smtp_id?: string | null;
  /**
   * Agent-editable copy for the fixed blocks the buyer form renders around the fields — the "does
   * not reserve the property" notice, the privacy notice, the submit button and the thank-you
   * screen. See `ExternalEoiWording`; anything missing/blank falls back to
   * `EXTERNAL_EOI_DEFAULT_WORDING`, so a config saved before these existed renders unchanged.
   */
  notice_title?: string;
  notice_text?: string;
  privacy_title?: string;
  privacy_text?: string;
  submit_label?: string;
  success_title?: string;
  success_text?: string;
  /**
   * How many columns the Property Details card lays its rows out in — reported as "increase the
   * width so we can have 3 columns at the top". Resolved through
   * `resolveExternalEoiPropertyColumns`, so a config saved before this existed still renders.
   */
  property_columns?: number;
  updated_at?: string;
}

/**
 * Every piece of fixed wording on the buyer form an agent can rewrite from Form Settings — reported
 * as "we need free text fields to ... amend the wording on the page": the free-text field type
 * covers copy *between* fields, but the notice / privacy / submit / thank-you blocks were still
 * hardcoded. `form_title`/`form_intro` were already configurable and are folded in so the builder,
 * its preview and the buyer form all resolve defaults from one place.
 */
export interface ExternalEoiWording {
  form_title: string;
  form_intro: string;
  notice_title: string;
  notice_text: string;
  privacy_title: string;
  privacy_text: string;
  submit_label: string;
  success_title: string;
  success_text: string;
}

export const EXTERNAL_EOI_DEFAULT_WORDING: ExternalEoiWording = {
  form_title: 'Expression of Interest',
  form_intro: 'Please complete the form below to express your interest in this property.',
  notice_title: 'This does not reserve the property.',
  notice_text:
    'The property remains on the market and available to other buyers until your initial deposit has been received and confirmed by your agent. Submitting this form registers your interest only.',
  privacy_title: 'Your information is protected.',
  privacy_text: 'The information you provide will only be used to process this enquiry and handled in accordance with our Privacy Policy.',
  submit_label: 'Submit Expression of Interest',
  success_title: 'Thank you!',
  success_text:
    'Your Expression of Interest has been submitted successfully. Your agent has been notified and will contact you shortly to discuss the next steps.',
};

/** The wording actually shown: whatever `source` overrides (non-blank), the defaults for the rest. */
export function resolveExternalEoiWording(source: Partial<ExternalEoiWording> | null | undefined): ExternalEoiWording {
  const out: ExternalEoiWording = { ...EXTERNAL_EOI_DEFAULT_WORDING };
  for (const key of Object.keys(out) as (keyof ExternalEoiWording)[]) {
    const value = source?.[key];
    if (typeof value === 'string' && value.trim()) out[key] = value;
  }
  return out;
}

/**
 * One of the office's own SMTP accounts (`user_smtps`), as offered in the
 * passcode-sender picker. Passwords are never returned by the endpoint.
 */
export interface ExternalEoiSmtpAccount {
  _id: string;
  name?: string;
  username?: string;
  /** Outgoing host, shown only as a disambiguator when two accounts share a name. */
  outgoing?: string;
}

/**
 * "Name (address)" — the option label in the passcode-sender picker.
 *
 * Either half can be missing on an account saved through an older settings
 * screen, so the label degrades to whichever one is present rather than
 * rendering a stray pair of brackets.
 */
export function externalEoiSmtpAccountLabel(account: ExternalEoiSmtpAccount | null | undefined): string {
  const name = (account?.name || '').trim();
  const username = (account?.username || '').trim();
  if (name && username) return `${name} (${username})`;
  return name || username || 'Unnamed account';
}

/** Status of a generated external EOI link. */
export type ExternalEoiLinkStatus =
  | 'not_generated'
  | 'link_generated'
  | 'awaiting_buyer'
  | 'partially_completed'
  | 'submitted'
  | 'reviewed'
  | 'expired'
  | 'cancelled';

/** A generated link row shown in the management table. */
export interface ExternalEoiLink {
  _id?: string;
  project_id: string;
  project_name?: string;
  lot_id?: string;
  lot_number?: string;
  contact_id?: string;
  buyer_name?: string;
  buyer_email?: string;
  /** Captured when the agent types a new purchaser instead of picking a contact. */
  buyer_mobile?: string;
  lead_id?: string;
  eoi_id?: string;
  /**
   * Whether the $2,000 initial deposit has been confirmed received.
   *
   * An EOI may exist before the deposit — but the lot must not be reserved or
   * taken off the market until this is true, and the buyer form warns that
   * submitting does not reserve the property while it is false.
   */
  deposit_received?: boolean;
  /** Opaque, non-guessable token used in the public URL. */
  token?: string;
  /** Fully-qualified public URL (built by the backend). */
  url?: string;
  /**
   * Slugs for the readable `/{project}/eoi/{lot}/{client}` URL, minted by the
   * backend so it can disambiguate two buyers with the same name on one
   * project (`rosinaayaz`, `rosinaayaz-2`, …). Absent on links created before
   * the readable URL existed — those keep using the token URL.
   */
  project_slug?: string;
  lot_slug?: string;
  client_slug?: string;
  status: ExternalEoiLinkStatus;
  generated_by?: string;
  generated_by_name?: string;
  generated_at?: string;
  expires_at?: string;
  opened_at?: string | null;
  submitted_at?: string | null;
  created_at?: string;
  /**
   * Fields added on top of the project's standard template for this one link only — e.g. a buyer
   * who needs a document the rest of the project's buyers don't. Catalogue-sourced, same shape as
   * the project template's own `fields`, so the buyer form and submission mapping treat them
   * identically; the backend appends them after the template fields when building the public form.
   */
  extra_fields?: ExternalEoiConfigField[];
  /**
   * Answers an agent typed into the link's form themselves ("View Link" → "Save for buyer") before
   * sending it — keyed by field key, merged over the CRM prefill when the buyer opens the link. See
   * external-eoi-client-review-2026-09-07-backend-requirements.md §1.
   */
  draft_answers?: Record<string, any>;
}

/**
 * Pull the token out of a public EOI URL, tolerating hash routing, query
 * strings and trailing slashes (older/other-environment payloads).
 */
export function extractExternalEoiToken(url?: string | null): string | null {
  if (!url) return null;
  const match = String(url).match(/external-eoi\/([^/?#]+)/);
  return match ? match[1] : null;
}

/**
 * Build the buyer-facing URL for a link.
 *
 * The backend-supplied `url` is deliberately NOT used verbatim: it can carry a
 * hash-routed path (`/#/external-eoi/…`) and whichever domain the API was
 * configured with. The app uses clean path routing, and a link copied while on
 * local/dev/live must point at that same environment — so the token is
 * extracted and the URL rebuilt against the caller's origin.
 */
export function buildExternalEoiPublicUrl(link: { token?: string; url?: string } | null | undefined, origin: string): string | null {
  const token = link?.token || extractExternalEoiToken(link?.url);
  if (!token) return null;
  return `${origin.replace(/\/+$/, '')}/external-eoi/${token}`;
}

/**
 * Lower-case, hyphenated, ASCII-only slug used in the readable public URL.
 * Spaces collapse rather than survive as hyphen runs, so "Rosina  Ayaz" and
 * "Rosina Ayaz" produce the same slug.
 */
export function slugifyExternalEoi(value: string | null | undefined): string {
  return String(value ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * The readable buyer-facing path: `/{project}/eoi/{lot}/{client}`.
 *
 * The backend mints the three slugs when the link is created (and appends
 * `-2`, `-3`… when a project already has a link for the same client name), so
 * they travel on the link rather than being derived here. They are only
 * rebuilt from the display names as a fallback for links generated before the
 * readable URL existed.
 *
 * Returns null when there is not enough to build a unique path — the caller
 * then falls back to the token URL, which never stops working.
 */
export function buildExternalEoiPrettyUrl(link: ExternalEoiLink | null | undefined, origin: string): string | null {
  if (!link) return null;
  const project = link.project_slug || slugifyExternalEoi(link.project_name);
  const lot = link.lot_slug || slugifyExternalEoi(link.lot_number);
  const client = link.client_slug || slugifyExternalEoi(link.buyer_name);
  if (!project || !lot || !client) return null;
  return `${origin.replace(/\/+$/, '')}/${project}/eoi/${lot}/${client}`;
}

/**
 * The URL to hand to a buyer: readable when the link carries its slugs, the
 * token URL otherwise. Both resolve to the same form.
 */
export function buildExternalEoiShareUrl(link: ExternalEoiLink | null | undefined, origin: string): string | null {
  return buildExternalEoiPrettyUrl(link, origin) || buildExternalEoiPublicUrl(link, origin);
}

/**
 * The EOI column a submitted field writes to.
 *
 * The agent's saved config carries `eoiField`, but a config persisted before a
 * catalogue entry gained its mapping (or one round-tripped through a backend
 * that dropped the key) will not. Falling back to the catalogue means the
 * mapping is never lost — which is what produced the empty EOI records
 * reported in round 2 §7.
 */
export function resolveExternalEoiField(field: { key: string; eoiField?: string } | null | undefined): string | null {
  if (!field?.key) return null;
  if (field.eoiField) return field.eoiField;
  return EXTERNAL_EOI_FIELD_CATALOG.find(c => c.key === field.key)?.eoiField ?? null;
}

/** A file answer as held in the form control and posted to the backend. */
export interface ExternalEoiFileValue {
  name: string;
  /** base64 data-URL. */
  data: string;
}

/** What the buyer form posts to `POST /external-eoi/public/submit/:token`. */
export interface ExternalEoiSubmission {
  /** Answers keyed by catalogue field key — the original contract. */
  values: Record<string, any>;
  /** The same answers keyed by EOI column, so the backend can write directly. */
  eoi_values: Record<string, any>;
  /** `key` → EOI column, for the keys actually present in this submission. */
  field_map: Record<string, string>;
}

/** True for an answer the buyer did not give (and which must never clear stored data). */
function isBlankAnswer(value: any): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  // File answers are `{ name, data }` — only real when both are present.
  if (typeof value === 'object' && !Array.isArray(value)) return !(value as ExternalEoiFileValue).data;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Expands every `columns` container in `fields` into its nested children (a `columns` field never
 * has an answer of its own) and passes everything else through unchanged. Depth-1 only — a `columns`
 * field can never contain another one, so one pass is enough.
 *
 * Shared by the buyer form's `buildForm()` and `buildExternalEoiSubmission()` below, so the two never
 * diverge on how a `columns` field's children are discovered.
 */
export function flattenExternalEoiAnswerableFields(fields: ExternalEoiConfigField[] | null | undefined): ExternalEoiConfigField[] {
  const out: ExternalEoiConfigField[] = [];
  for (const field of fields || []) {
    if (!field) continue;
    if (field.type === 'columns') {
      for (const slot of field.columns || []) out.push(...slot);
    } else {
      out.push(field);
    }
  }
  return out;
}

/**
 * Build the submit payload from the configured fields and the raw form value.
 *
 * Two deliberate rules, both of them bug fixes:
 *
 * 1. **Blanks are omitted.** An existing EOI is partially merged (round 2 §8),
 *    so an empty answer must read as "not supplied" rather than "clear this".
 *    Sending `''` risks wiping a value an agent typed by hand.
 * 2. **The mapping travels with the data.** `eoi_values` and `field_map` carry
 *    the `key → eoiField` resolution the frontend already owns, so the backend
 *    does not have to re-derive it from a stale link snapshot.
 *
 * `values` is still sent unchanged in shape for backwards compatibility.
 * Property (`info`) fields are display-only and never submitted, same as `static_text`/`divider`
 * (both layout-only) and a `columns` container itself (flattened away below — its children are
 * still submitted like any other field).
 */
export function buildExternalEoiSubmission(
  fields: ExternalEoiConfigField[] | null | undefined,
  rawValues: Record<string, any> | null | undefined
): ExternalEoiSubmission {
  const values: Record<string, any> = {};
  const eoi_values: Record<string, any> = {};
  const field_map: Record<string, string> = {};

  for (const field of flattenExternalEoiAnswerableFields(fields)) {
    if (!field || field.type === 'info' || field.type === 'static_text' || field.type === 'divider') continue;

    const value = (rawValues || {})[field.key];
    if (isBlankAnswer(value)) continue;

    values[field.key] = value;

    const eoiField = resolveExternalEoiField(field);
    if (eoiField) {
      eoi_values[eoiField] = value;
      field_map[field.key] = eoiField;
    }
  }

  return { values, eoi_values, field_map };
}

/** Nice human labels + badge colours for each status. */
export const EXTERNAL_EOI_STATUS_META: Record<ExternalEoiLinkStatus, { label: string; badge: string }> = {
  not_generated: { label: 'Not Generated', badge: 'badge-secondary' },
  link_generated: { label: 'Link Generated', badge: 'badge-info' },
  awaiting_buyer: { label: 'Awaiting Buyer', badge: 'badge-warning' },
  partially_completed: { label: 'Partially Completed', badge: 'badge-warning' },
  submitted: { label: 'Submitted', badge: 'badge-success' },
  reviewed: { label: 'Reviewed', badge: 'badge-primary' },
  expired: { label: 'Expired', badge: 'badge-secondary' },
  // "Deactivated" is the word used on the button that produces this state, so
  // the badge, the toast and the buyer-facing page all agree.
  cancelled: { label: 'Link Deactivated', badge: 'badge-danger' },
};

/**
 * Master catalogue of every field that can be added to an external EOI form.
 * Mirrors the fields available on the standard Remmi EOI form (buyer,
 * property, financial and supporting-document fields).
 *
 * NOTE: keep `key` values stable — they are persisted in project config and
 * used to map submissions back onto the EOI record on the backend.
 */
export const EXTERNAL_EOI_FIELD_CATALOG: ExternalEoiCatalogField[] = [
  // ----- Property information (read-only info fields) -----------------------
  {
    key: 'project_name',
    label: 'Project Name',
    icon: 'fa-solid fa-building',
    type: 'info',
    section: 'property',
    eoiField: 'projectid',
    defaultPrefill: 'project.name',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_number',
    label: 'Lot Number',
    icon: 'fa-solid fa-map-pin',
    type: 'info',
    section: 'property',
    eoiField: 'unit_id',
    defaultPrefill: 'lot.number',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_price',
    label: 'Price',
    icon: 'fa-solid fa-tag',
    type: 'info',
    section: 'property',
    eoiField: 'total_price',
    defaultPrefill: 'lot.price',
    readOnlyByDefault: true,
  },
  {
    key: 'project_location',
    label: 'Location',
    icon: 'fa-solid fa-location-crosshairs',
    type: 'info',
    section: 'property',
    defaultPrefill: 'project.location',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_bed',
    label: 'Bed',
    icon: 'fa-solid fa-bed',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.bed',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_bath',
    label: 'Bath',
    icon: 'fa-solid fa-bath',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.bath',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_car',
    label: 'Car',
    icon: 'fa-solid fa-car',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.car',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_type',
    label: 'Type',
    icon: 'fa-solid fa-building',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.type',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_level',
    label: 'Level',
    icon: 'fa-solid fa-layer-group',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.level',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_aspect',
    label: 'Aspect',
    icon: 'fa-solid fa-compass',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.aspect',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_internal_area',
    label: 'Internal Area',
    icon: 'fa-solid fa-ruler-combined',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.internal_area',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_external_area',
    label: 'External Area',
    icon: 'fa-solid fa-ruler-horizontal',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.external_area',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_total_area',
    label: 'Total Area',
    icon: 'fa-solid fa-vector-square',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.total_area',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_internal_price_m2',
    label: 'Internal Price M2',
    icon: 'fa-solid fa-dollar-sign',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.internal_price_m2',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_storage',
    label: 'Storage',
    icon: 'fa-solid fa-box-archive',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.storage',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_study',
    label: 'Study',
    icon: 'fa-solid fa-book-open',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.study',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_mpr',
    label: 'MPR',
    icon: 'fa-solid fa-couch',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.mpr',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_orientation',
    label: 'Orientation',
    icon: 'fa-solid fa-location-arrow',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.orientation',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_car_park_price',
    label: 'Car Park Price',
    icon: 'fa-solid fa-square-parking',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.car_park_price',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_storage_price',
    label: 'Storage Price',
    icon: 'fa-solid fa-boxes-stacked',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.storage_price',
    readOnlyByDefault: true,
  },
  {
    key: 'lot_total_price',
    label: 'Total Price',
    icon: 'fa-solid fa-coins',
    type: 'info',
    section: 'property',
    eoiField: 'total_price',
    defaultPrefill: 'lot.total_price',
    readOnlyByDefault: true,
  },
  {
    // Available but off by default — an internal sales state like "Held" reads
    // oddly to a buyer, so the agent opts in.
    key: 'lot_status',
    label: 'Status',
    icon: 'fa-solid fa-circle-info',
    type: 'info',
    section: 'property',
    defaultPrefill: 'lot.status',
    readOnlyByDefault: true,
  },

  // ----- Buyer details ------------------------------------------------------
  {
    key: 'full_name',
    label: 'Full Name',
    icon: 'fa-solid fa-user',
    type: 'text',
    section: 'buyer',
    eoiField: 'client_first_name',
    defaultPrefill: 'contact.full_name',
    recommendedRequired: true,
  },
  {
    key: 'email',
    label: 'Email Address',
    icon: 'fa-solid fa-envelope',
    type: 'email',
    section: 'buyer',
    eoiField: 'email',
    defaultPrefill: 'contact.email',
    recommendedRequired: true,
  },
  {
    key: 'phone_mobile',
    label: 'Phone (Mobile)',
    icon: 'fa-solid fa-mobile-screen',
    type: 'tel',
    section: 'buyer',
    eoiField: 'mobile_no',
    defaultPrefill: 'contact.mobile',
    recommendedRequired: true,
  },
  {
    key: 'phone_home',
    label: 'Phone (Home)',
    icon: 'fa-solid fa-phone',
    type: 'tel',
    section: 'buyer',
    eoiField: 'phone_no',
    defaultPrefill: 'contact.phone',
  },
  {
    key: 'current_address',
    label: 'Current Address',
    icon: 'fa-solid fa-location-dot',
    type: 'address',
    section: 'buyer',
    eoiField: 'address',
    defaultPrefill: 'contact.address',
  },
  { key: 'address_line_1', label: 'Address Line 1', icon: 'fa-solid fa-location-dot', type: 'text', section: 'buyer', eoiField: 'address_line_1' },
  { key: 'address_line_2', label: 'Address Line 2', icon: 'fa-solid fa-location-dot', type: 'text', section: 'buyer', eoiField: 'address_line_2' },
  { key: 'suburb', label: 'Suburb', icon: 'fa-solid fa-city', type: 'text', section: 'buyer', eoiField: 'suburb' },
  { key: 'state', label: 'State', icon: 'fa-solid fa-map', type: 'text', section: 'buyer', eoiField: 'state' },
  { key: 'postcode', label: 'Postcode', icon: 'fa-solid fa-hashtag', type: 'text', section: 'buyer', eoiField: 'postcode' },
  {
    key: 'country',
    label: 'Country',
    icon: 'fa-solid fa-earth-oceania',
    type: 'text',
    section: 'buyer',
    eoiField: 'country',
  },
  { key: 'date_of_birth', label: 'Date of Birth', icon: 'fa-solid fa-cake-candles', type: 'date', section: 'buyer', eoiField: 'date_of_birth' },
  {
    key: 'occupation',
    label: 'Occupation',
    icon: 'fa-solid fa-briefcase',
    type: 'text',
    section: 'buyer',
    eoiField: 'occupation',
  },
  { key: 'employer', label: 'Employer', icon: 'fa-solid fa-building-user', type: 'text', section: 'buyer', eoiField: 'employer' },
  {
    key: 'nationality',
    label: 'Nationality',
    icon: 'fa-solid fa-flag',
    type: 'text',
    section: 'buyer',
    eoiField: 'nationality_purchaser_1',
  },
  {
    key: 'firb',
    label: 'FIRB Required?',
    icon: 'fa-solid fa-passport',
    type: 'select',
    section: 'buyer',
    eoiField: 'firb',
    options: ['Yes', 'No'],
  },
  {
    key: 'firb_nationality',
    label: 'Nationality (if FIRB applies)',
    icon: 'fa-solid fa-flag',
    type: 'text',
    section: 'buyer',
    eoiField: 'firb_nationality',
    helpTextByDefault: 'Only needed if Foreign Interest (FIRB) is Yes.',
  },
  {
    key: 'buyer_purchasing_for',
    label: 'Buyer Purchasing For',
    icon: 'fa-solid fa-house-circle-check',
    type: 'radio',
    section: 'buyer',
    eoiField: 'buyer_purchasing_for',
    options: ['Owner Occupier', 'Investment Purposes'],
  },
  {
    key: 'company_name',
    label: 'Company Name',
    icon: 'fa-solid fa-building',
    type: 'text',
    section: 'buyer',
    eoiField: 'company_name',
    helpTextByDefault: 'If purchasing as a company, name its director(s) and their contact details in Your Details above.',
  },
  { key: 'company_acn', label: 'ACN', icon: 'fa-solid fa-hashtag', type: 'text', section: 'buyer', eoiField: 'company_acn' },
  { key: 'trust_name', label: 'Name of Trust', icon: 'fa-solid fa-scale-balanced', type: 'text', section: 'buyer', eoiField: 'trust_name' },
  {
    key: 'email_purchaser_2',
    label: 'Email Address (Purchaser 2)',
    icon: 'fa-solid fa-envelope',
    type: 'email',
    section: 'buyer',
    eoiField: 'email_purchaser_2',
  },
  {
    key: 'phone_purchaser_2',
    label: 'Phone Number (Purchaser 2)',
    icon: 'fa-solid fa-mobile-screen',
    type: 'tel',
    section: 'buyer',
    eoiField: 'phone_purchaser_2',
  },
  {
    key: 'date_of_birth_purchaser_2',
    label: 'Date of Birth (Purchaser 2)',
    icon: 'fa-solid fa-cake-candles',
    type: 'date',
    section: 'buyer',
    eoiField: 'date_of_birth_purchaser_2',
  },

  // ----- Financial information ---------------------------------------------
  {
    key: 'source_of_funds',
    label: 'Source of Funds',
    icon: 'fa-solid fa-money-bill-wave',
    // Grouped under "Your Details" to match the buyer-form design.
    section: 'buyer',
    type: 'select',
    eoiField: 'source_of_funds',
    options: ['Savings', 'Sale of Property', 'Loan / Mortgage', 'Investment', 'Gift / Inheritance', 'Other'],
  },
  {
    key: 'income_range',
    label: 'Income Range',
    icon: 'fa-solid fa-chart-line',
    type: 'select',
    section: 'financial',
    eoiField: 'income_range',
    options: ['Under $75,000', '$75,000 - $120,000', '$120,000 - $200,000', '$200,000 - $350,000', 'Over $350,000'],
  },
  {
    key: 'purchase_type',
    label: 'Purchase Type',
    icon: 'fa-solid fa-house-circle-check',
    type: 'select',
    section: 'financial',
    eoiField: 'purchase_type',
    options: ['Owner Occupier', 'Investment', 'First Home Buyer'],
  },
  {
    key: 'deposit_amount',
    label: 'Deposit Amount',
    icon: 'fa-solid fa-sack-dollar',
    type: 'number',
    section: 'financial',
    eoiField: 'initial_deposit',
  },
  {
    key: 'initial_deposit_choice',
    label: 'Initial Deposit',
    icon: 'fa-solid fa-sack-dollar',
    type: 'radio',
    section: 'financial',
    eoiField: 'initial_deposit_choice',
    options: ['$2,000', '$5,000'],
  },

  // ----- Supporting documents (file uploads) -------------------------------
  {
    key: 'upload_identification',
    label: 'Upload Identification',
    icon: 'fa-solid fa-id-card',
    type: 'file',
    section: 'documents',
    eoiField: 'buyers_id',
    fileConfig: { accept: '.pdf,.jpg,.jpeg,.png', maxSizeMb: 10 },
  },
  {
    key: 'upload_financial_docs',
    label: 'Upload Financial Documents',
    icon: 'fa-solid fa-file-invoice-dollar',
    type: 'file',
    section: 'documents',
    eoiField: 'upload_other_documents',
    fileConfig: { accept: '.pdf,.jpg,.jpeg,.png', maxSizeMb: 10 },
  },
  {
    key: 'signed_reservation',
    label: 'Signed Reservation Form',
    icon: 'fa-solid fa-file-signature',
    type: 'file',
    section: 'documents',
    eoiField: 'signed_reservation_agreementform',
    fileConfig: { accept: '.pdf,.jpg,.jpeg,.png', maxSizeMb: 10 },
  },
  {
    // The headline supporting document. Deliberately NOT recommendedRequired —
    // the slip is often sent to the agent by text or email instead.
    key: 'deposit_receipt',
    label: 'Deposit Slip',
    icon: 'fa-solid fa-receipt',
    type: 'file',
    section: 'documents',
    eoiField: 'deposit_receipt',
    fileConfig: { accept: '.pdf,.jpg,.jpeg,.png', maxSizeMb: 10 },
    helpTextByDefault: 'Optional — if you have already sent this to your agent you can skip it.',
  },

  // ===========================================================================
  // Everything below mirrors the remaining controls on the EOI record's own
  // form (edit-eoi.component.ts), so an agent can put ANY EOI field on an
  // external form and have the answer written straight back to the EOI.
  //
  // Two groups are deliberately absent, because they are not answerable data:
  //   * `admin_comments_user` / `comments_user` / `agent_comments_user` —
  //     stamps recording WHO wrote a comment, set on save.
  //   * `carpark_included` / `carpark_added` / `storage_included` /
  //     `storage_added` — multi-selects over the project's car park and
  //     storage inventory, transformed into `carpark_number`/`carpark_storage`
  //     on save. A free-typed string would not survive that transform.
  // ===========================================================================

  // ----- Sale details -------------------------------------------------------
  {
    key: 'sale_type',
    label: 'Sale Type',
    icon: 'fa-solid fa-handshake',
    type: 'select',
    section: 'sale',
    eoiField: 'sale_type',
    options: ['Retail', 'Agent'],
  },
  {
    key: 'contract_price',
    label: 'Contract Price',
    icon: 'fa-solid fa-file-invoice-dollar',
    type: 'number',
    section: 'sale',
    eoiField: 'contract_price',
  },
  { key: 'total_deposit', label: 'Total Deposit', icon: 'fa-solid fa-sack-dollar', type: 'number', section: 'sale', eoiField: 'total_deposit' },
  {
    key: 'balance_of_deposit',
    label: 'Balance of Deposit',
    icon: 'fa-solid fa-scale-balanced',
    type: 'number',
    section: 'sale',
    eoiField: 'balance_of_deposit',
  },
  {
    key: 'deposit_comments',
    label: 'Deposit Comments',
    icon: 'fa-solid fa-comment-dollar',
    type: 'textarea',
    section: 'sale',
    eoiField: 'deposit_comments',
  },
  {
    key: 'deductible_incentives',
    label: 'Deductible Incentives',
    icon: 'fa-solid fa-percent',
    type: 'text',
    section: 'sale',
    eoiField: 'deductible_incentives',
  },
  {
    key: 'deductible_incentives_price',
    label: 'Deductible Incentives Price',
    icon: 'fa-solid fa-tags',
    type: 'number',
    section: 'sale',
    eoiField: 'deductible_incentives_price',
  },
  // Labelled "(Contract)" — the Project Options catalogue above already has a read-only "Car Park
  // Price" / "Storage Price" (`lot_car_park_price` / `lot_storage_price`) auto-filled from the
  // lot's listing data. These are a *different* field: a blank box for an agent to key in the
  // contract figure. Identical labels meant a buyer-facing form ended up with the fillable
  // contract field where the fixed listing price was wanted — an admin picking "Car Park Price"
  // from the builder had no way to tell the two apart.
  {
    key: 'carpark_price',
    label: 'Car Park Price (Contract)',
    icon: 'fa-solid fa-square-parking',
    type: 'number',
    section: 'sale',
    eoiField: 'carpark_price',
  },
  {
    key: 'storage_price',
    label: 'Storage Price (Contract)',
    icon: 'fa-solid fa-boxes-stacked',
    type: 'number',
    section: 'sale',
    eoiField: 'storage_price',
  },
  { key: 'subtotal', label: 'Subtotal', icon: 'fa-solid fa-calculator', type: 'number', section: 'sale', eoiField: 'subtotal' },
  { key: 'upgrade_group', label: 'Upgrade Group', icon: 'fa-solid fa-layer-group', type: 'text', section: 'sale', eoiField: 'upgrade_group_1' },
  {
    key: 'upgrade_group_price',
    label: 'Upgrade Group Price',
    icon: 'fa-solid fa-dollar-sign',
    type: 'number',
    section: 'sale',
    eoiField: 'upgrade_group_1_price',
  },
  { key: 'upgrades_total', label: 'Upgrades Total', icon: 'fa-solid fa-arrow-trend-up', type: 'number', section: 'sale', eoiField: 'upgrades_total' },
  {
    key: 'other_upgrade_group',
    label: 'Other Upgrade Group',
    icon: 'fa-solid fa-layer-group',
    type: 'text',
    section: 'sale',
    eoiField: 'other_upgrade_group',
  },
  {
    key: 'other_upgrade_type',
    label: 'Other Upgrade Type',
    icon: 'fa-solid fa-screwdriver-wrench',
    type: 'text',
    section: 'sale',
    eoiField: 'other_upgrade_type',
  },
  {
    key: 'other_upgrade_price',
    label: 'Other Upgrade Price',
    icon: 'fa-solid fa-dollar-sign',
    type: 'number',
    section: 'sale',
    eoiField: 'other_upgrade_price',
  },
  { key: 'bonus', label: 'Bonus', icon: 'fa-solid fa-gift', type: 'number', section: 'sale', eoiField: 'bonus' },
  { key: 'bonus_campaign', label: 'Bonus Campaign', icon: 'fa-solid fa-bullhorn', type: 'text', section: 'sale', eoiField: 'bonus_campaign' },
  {
    key: 'bonus_payable_upon',
    label: 'Bonus Payable Upon',
    icon: 'fa-solid fa-clipboard-check',
    type: 'text',
    section: 'sale',
    eoiField: 'bonus_payable_upon',
  },
  { key: 'bonus_payable_to', label: 'Bonus Payable To', icon: 'fa-solid fa-user-check', type: 'text', section: 'sale', eoiField: 'bonus_payable_to' },
  { key: 'bonus_comments', label: 'Bonus Comments', icon: 'fa-solid fa-comment-dots', type: 'textarea', section: 'sale', eoiField: 'bonus_comments' },
  {
    key: 'colour_scheme',
    label: 'Colour Scheme',
    icon: 'fa-solid fa-palette',
    type: 'radio',
    section: 'sale',
    eoiField: 'colour_scheme',
    options: ['Light (Margaret)', 'Dark (William)', 'Executive Colour Scheme'],
    helpTextByDefault: 'Executive Colour Scheme is only applicable to Penthouse levels.',
  },
  {
    key: 'upgrade_finishes',
    label: 'Optional Upgrades — Finishes',
    icon: 'fa-solid fa-swatchbook',
    type: 'checkbox',
    section: 'sale',
    eoiField: 'upgrade_finishes',
    options: ['Emerald to Ruby', 'Ruby to Diamond'],
  },
  {
    key: 'upgrade_appliances',
    label: 'Optional Upgrades — Appliances',
    icon: 'fa-solid fa-blender',
    type: 'checkbox',
    section: 'sale',
    eoiField: 'upgrade_appliances',
    options: ['Emerald to Ruby', 'Ruby to Gaggenau', 'Ruby to Diamond', 'Diamond to Gaggenau'],
  },
  {
    key: 'upgrade_lighting_automation',
    label: 'Optional Upgrades — Lighting Remote Automation',
    icon: 'fa-solid fa-lightbulb',
    type: 'checkbox',
    section: 'sale',
    eoiField: 'upgrade_lighting_automation',
    options: ['Emerald and Ruby to Diamond'],
  },
  {
    key: 'upgrade_extra',
    label: 'Optional Upgrades — Extra',
    icon: 'fa-solid fa-plus',
    type: 'checkbox',
    section: 'sale',
    eoiField: 'upgrade_extra',
    options: [
      'European Brand Integrated Side by Side Fridge/Freezer',
      'Boiling and Chilled Water Tap to Kitchen',
      'Pendant Light to Kitchen Option',
      'Wine Fridge',
    ],
  },
  {
    key: 'upgrade_price_total',
    label: 'Upgrade Price',
    icon: 'fa-solid fa-dollar-sign',
    type: 'number',
    section: 'sale',
    eoiField: 'upgrade_price_total',
  },

  // ----- Additional purchasers ---------------------------------------------
  { key: 'purchaser_1', label: 'Purchaser 1', icon: 'fa-solid fa-user', type: 'text', section: 'buyer', eoiField: 'purchaser_1' },
  { key: 'purchaser_2', label: 'Purchaser 2', icon: 'fa-solid fa-user-group', type: 'text', section: 'buyer', eoiField: 'purchaser_2' },
  { key: 'purchaser_3', label: 'Purchaser 3', icon: 'fa-solid fa-user-group', type: 'text', section: 'buyer', eoiField: 'purchaser_3' },
  {
    key: 'nationality_purchaser_2',
    label: 'Nationality (Purchaser 2)',
    icon: 'fa-solid fa-flag',
    type: 'text',
    section: 'buyer',
    eoiField: 'nationality_purchaser_2',
  },
  {
    key: 'nationality_purchaser_3',
    label: 'Nationality (Purchaser 3)',
    icon: 'fa-solid fa-flag',
    type: 'text',
    section: 'buyer',
    eoiField: 'nationality_purchaser_3',
  },

  // ----- Critical dates -----------------------------------------------------
  { key: 'date_received', label: 'Date Received', icon: 'fa-solid fa-calendar-day', type: 'date', section: 'dates', eoiField: 'date_received' },
  { key: 'contract_date', label: 'Contract Date', icon: 'fa-solid fa-file-contract', type: 'date', section: 'dates', eoiField: 'contract_date' },
  {
    key: 'contract_issued_date',
    label: 'Contract Issued Date',
    icon: 'fa-solid fa-file-export',
    type: 'date',
    section: 'dates',
    eoiField: 'contract_issued_date',
  },
  {
    key: 'contract_due_date',
    label: 'Contract Due Date',
    icon: 'fa-solid fa-calendar-check',
    type: 'date',
    section: 'dates',
    eoiField: 'contract_due_date',
  },
  {
    key: 'contract_received_date',
    label: 'Contract Received Date',
    icon: 'fa-solid fa-file-import',
    type: 'date',
    section: 'dates',
    eoiField: 'contract_received_date',
  },
  {
    key: 'cool_off_expiry_date',
    label: 'Cool Off Expiry Date',
    icon: 'fa-solid fa-hourglass-half',
    type: 'date',
    section: 'dates',
    eoiField: 'cool_off_expiry_date',
  },
  {
    key: 'bal_deposit_due_date',
    label: 'Balance Deposit Due Date',
    icon: 'fa-solid fa-calendar-plus',
    type: 'date',
    section: 'dates',
    eoiField: 'bal_deposit_due_date',
  },
  {
    key: 'finance_due_date',
    label: 'Finance Due Date',
    icon: 'fa-solid fa-building-columns',
    type: 'date',
    section: 'dates',
    eoiField: 'finance_due_date',
  },
  { key: 'firb_due_date', label: 'FIRB Due Date', icon: 'fa-solid fa-passport', type: 'date', section: 'dates', eoiField: 'firb_due_date' },
  {
    key: 'unconditional_date',
    label: 'Unconditional Date',
    icon: 'fa-solid fa-circle-check',
    type: 'date',
    section: 'dates',
    eoiField: 'unconditional_date',
  },
  {
    key: 'projected_settlement_date',
    label: 'Projected Settlement Date',
    icon: 'fa-solid fa-calendar-days',
    type: 'date',
    section: 'dates',
    eoiField: 'projected_settlement_date',
  },
  {
    key: 'settlement_date',
    label: 'Settlement Date',
    icon: 'fa-solid fa-handshake-angle',
    type: 'date',
    section: 'dates',
    eoiField: 'settlement_date',
  },
  {
    key: 'balance_deposit_paid',
    label: 'Balance Deposit Paid',
    icon: 'fa-solid fa-money-check-dollar',
    type: 'select',
    section: 'dates',
    eoiField: 'balance_deposit_paid',
    options: ['Yes', 'No'],
  },
  {
    key: 'finance_approved',
    label: 'Finance Approved',
    icon: 'fa-solid fa-circle-check',
    type: 'select',
    section: 'dates',
    eoiField: 'finance_approved',
    options: ['Yes', 'No'],
  },
  {
    key: 'firb_approved',
    label: 'FIRB Approved',
    icon: 'fa-solid fa-stamp',
    type: 'select',
    section: 'dates',
    eoiField: 'firb_approved',
    options: ['Yes', 'No'],
  },
  {
    key: 'additional_condition',
    label: 'Additional Condition',
    icon: 'fa-solid fa-list-check',
    type: 'textarea',
    section: 'dates',
    eoiField: 'additional_condition',
  },
  {
    key: 'additional_condition_satisfied',
    label: 'Additional Condition Satisfied',
    icon: 'fa-solid fa-square-check',
    type: 'select',
    section: 'dates',
    eoiField: 'additional_condition_satisfied',
    options: ['Yes', 'No'],
  },

  // ----- Solicitor ----------------------------------------------------------
  { key: 'solicitor_firm', label: 'Solicitor Firm', icon: 'fa-solid fa-scale-balanced', type: 'text', section: 'solicitor', eoiField: 'firm' },
  { key: 'solicitor_name', label: 'Solicitor Name', icon: 'fa-solid fa-user-tie', type: 'text', section: 'solicitor', eoiField: 'solicitor_name' },
  {
    key: 'solicitor_phone',
    label: 'Solicitor Phone Number',
    icon: 'fa-solid fa-phone',
    type: 'tel',
    section: 'solicitor',
    eoiField: 'solicitor_phone',
  },
  {
    key: 'solicitor_email',
    label: 'Solicitor Email Address',
    icon: 'fa-solid fa-envelope',
    type: 'email',
    section: 'solicitor',
    eoiField: 'solicitor_email',
  },
  {
    key: 'solicitor_address',
    label: 'Solicitor Full Address',
    icon: 'fa-solid fa-location-dot',
    type: 'address',
    section: 'solicitor',
    eoiField: 'solicitor_address',
  },

  // ----- Agency -------------------------------------------------------------
  { key: 'agency', label: 'Agency', icon: 'fa-solid fa-building', type: 'text', section: 'agency', eoiField: 'agency' },
  { key: 'agent', label: 'Agent', icon: 'fa-solid fa-user-tie', type: 'text', section: 'agency', eoiField: 'agent' },
  { key: 'internal_team', label: 'Internal Team', icon: 'fa-solid fa-users', type: 'text', section: 'agency', eoiField: 'internal_team' },
  { key: 'internal_agent', label: 'Internal Agent', icon: 'fa-solid fa-user-gear', type: 'text', section: 'agency', eoiField: 'internal_agent' },
  {
    key: 'internal_agent_2',
    label: 'Internal Agent 2',
    icon: 'fa-solid fa-user-gear',
    type: 'text',
    section: 'agency',
    eoiField: 'internal_agent_2',
  },
  {
    key: 'internal_agent_3',
    label: 'Internal Agent 3',
    icon: 'fa-solid fa-user-gear',
    type: 'text',
    section: 'agency',
    eoiField: 'internal_agent_3',
  },
  { key: 'reservation_code', label: 'Reservation Code', icon: 'fa-solid fa-barcode', type: 'text', section: 'agency', eoiField: 'reservation_code' },
  {
    key: 'identification_sighted',
    label: "Salesperson Has Sighted Buyer/s' Identification",
    icon: 'fa-solid fa-id-card-clip',
    type: 'radio',
    section: 'agency',
    eoiField: 'identification_sighted',
    options: ['Yes', 'No'],
  },

  // ----- Internal / admin ---------------------------------------------------
  // Grouped apart so an agent does not put an internal field on a buyer-facing
  // form by accident.
  { key: 'status_reason', label: 'Status Reason', icon: 'fa-solid fa-circle-info', type: 'text', section: 'internal', eoiField: 'status_reason' },
  { key: 'ypm_sale', label: 'Non YPM Sale', icon: 'fa-solid fa-store', type: 'text', section: 'internal', eoiField: 'ypm_sale' },
  {
    key: 'cancelled',
    label: 'Cancelled',
    icon: 'fa-solid fa-ban',
    type: 'select',
    section: 'internal',
    eoiField: 'cancelled',
    options: ['Yes', 'No'],
  },
  {
    key: 'cancelled_date',
    label: 'Cancelled Date',
    icon: 'fa-solid fa-calendar-xmark',
    type: 'date',
    section: 'internal',
    eoiField: 'cancelled_date',
  },
  {
    key: 'cancelled_reason',
    label: 'Cancelled Reason',
    icon: 'fa-solid fa-comment-slash',
    type: 'textarea',
    section: 'internal',
    eoiField: 'cancelled_reason',
  },
  { key: 'agent_comments', label: 'Agent Comments', icon: 'fa-solid fa-comments', type: 'textarea', section: 'internal', eoiField: 'agent_comments' },
  {
    key: 'admin_only_comments',
    label: 'Admin Only Comments',
    icon: 'fa-solid fa-user-shield',
    type: 'textarea',
    section: 'internal',
    eoiField: 'admin_only_comments',
  },
  {
    key: 'sales_related_notes',
    label: 'Sales Related Notes',
    icon: 'fa-solid fa-note-sticky',
    type: 'textarea',
    section: 'internal',
    eoiField: 'sales_related_notes',
  },
  {
    key: 'commission_comments',
    label: 'Commission Comments',
    icon: 'fa-solid fa-percent',
    type: 'textarea',
    section: 'internal',
    eoiField: 'commission_comments',
  },

  // ----- Other --------------------------------------------------------------
  {
    key: 'how_did_you_hear',
    label: 'How did you hear about us?',
    icon: 'fa-solid fa-bullhorn',
    type: 'select',
    section: 'other',
    eoiField: 'referral_source',
    options: ['Online Search', 'Social Media', 'Referral', 'Agent', 'Signage', 'Advertisement', 'Other'],
  },
  {
    key: 'additional_comments',
    label: 'Additional Comments',
    icon: 'fa-solid fa-comment-dots',
    type: 'textarea',
    // Sits beside the upload under "Supporting Information" in the design.
    section: 'documents',
    eoiField: 'comments',
  },
];

// ===========================================================================
// Property Details card — shared derivation used by both the buyer form's real card and the
// builder canvas's own preview rendering of it. Split out so the two can never quietly disagree on
// which fields are "meta" vs "price" vs "regular", or how a value gets formatted/labelled/iconed.
// ===========================================================================

/**
 * Identity fields — rendered as label-over-value along the top of the Property Details card, not
 * buried in the spec grid below. Status belongs here too: "For Sale" is a headline like Lot/
 * Location, not a measurement like Bed or Aspect.
 */
export const EXTERNAL_EOI_PROPERTY_META_KEYS = ['project_name', 'lot_number', 'project_location', 'lot_status'];

/** Dollar figures — kept full-width and standalone, one per row, in the Property Details card. */
export const EXTERNAL_EOI_PRICE_KEYS = ['lot_price', 'lot_car_park_price', 'lot_storage_price', 'lot_total_price'];

/** Column counts offered for the Property Details card in Form Settings. */
export const EXTERNAL_EOI_PROPERTY_COLUMN_OPTIONS = [2, 3, 4];

/**
 * Three, not the two this card used to be hardcoded to: "increase the width so we can have 3
 * columns at the top". A project that has never touched the setting adopts it.
 */
export const EXTERNAL_EOI_DEFAULT_PROPERTY_COLUMNS = 3;

/**
 * The Property Details card's column count for one config. Read by the buyer form, the builder
 * canvas and the builder's preview alike, so the three can't drift apart — and by the SCSS through
 * `externalEoiPropertyColumnsClass`, which is what draws the vertical rule between the columns
 * ("where is the break in the columns, it is just one line border").
 */
export function resolveExternalEoiPropertyColumns(source: { property_columns?: any } | null | undefined): number {
  const value = Number(source?.property_columns);
  return EXTERNAL_EOI_PROPERTY_COLUMN_OPTIONS.includes(value) ? value : EXTERNAL_EOI_DEFAULT_PROPERTY_COLUMNS;
}

/**
 * Modifier class carrying the column count into the stylesheet.
 *
 * A class rather than an inline `grid-template-columns`: the column dividers are `nth-child`
 * rules that have to know how many items make up a row, and `nth-child` can't be driven from a
 * style binding.
 */
export function externalEoiPropertyColumnsClass(columns: number): string {
  return `is-cols-${resolveExternalEoiPropertyColumns({ property_columns: columns })}`;
}

/** Where each property prefill source reads from on a `property` payload (real or the builder's own preview sample). */
export const EXTERNAL_EOI_PROPERTY_SOURCE_MAP: Record<string, string> = {
  'project.name': 'project_name',
  'project.location': 'location',
  'lot.number': 'lot_number',
  'lot.price': 'price',
  'lot.bed': 'bed',
  'lot.bath': 'bath',
  'lot.car': 'car',
  'lot.storage': 'storage',
  'lot.study': 'study',
  'lot.mpr': 'mpr',
  'lot.type': 'type',
  'lot.level': 'level',
  'lot.aspect': 'aspect',
  'lot.orientation': 'orientation',
  'lot.internal_area': 'internal_area',
  'lot.external_area': 'external_area',
  'lot.total_area': 'total_area',
  'lot.internal_price_m2': 'internal_price_m2',
  'lot.car_park_price': 'car_park_price',
  'lot.storage_price': 'storage_price',
  'lot.total_price': 'total_price',
  'lot.status': 'status',
};

/** Dollar fields — plain "750000" reads as a bug, not a price, next to a real estate offer. */
const EXTERNAL_EOI_CURRENCY_KEYS = new Set(['lot_price', 'lot_total_price', 'lot_car_park_price', 'lot_storage_price', 'lot_internal_price_m2']);
/** Fields measured in square metres. */
const EXTERNAL_EOI_AREA_KEYS = new Set(['lot_internal_area', 'lot_external_area', 'lot_total_area']);

/** Formats a resolved property value for display — currency/m² for the keys that need it, the raw value otherwise. */
export function formatExternalEoiInfoValue(key: string, value: unknown): string {
  const isCurrency = EXTERNAL_EOI_CURRENCY_KEYS.has(key);
  const isArea = EXTERNAL_EOI_AREA_KEYS.has(key);
  if (!isCurrency && !isArea) return String(value);

  // The backend has sent this pre-formatted before ("$750,000"); stripping non-numerics before
  // parsing makes re-formatting a no-op in that case instead of doubling up on punctuation.
  const num = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(num)) return String(value);

  if (isCurrency) {
    const formatted = num.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
    return key === 'lot_internal_price_m2' ? `${formatted}/m²` : formatted;
  }
  return `${num.toLocaleString('en-AU')} m²`;
}

/**
 * Alternate names the backend has used for the same lot value.
 *
 * The lot record and the public payload do not agree on every key (the lot screen calls it
 * `Lot_Price`, the payload `price`) — see `resolveExternalEoiInfoValue`, which is the only reader of
 * this table. Shared here (rather than living on the buyer form component alone) so the PDF export
 * resolves a property field's value exactly the same way the live form does.
 */
export const EXTERNAL_EOI_PROPERTY_KEY_ALIASES: Record<string, string[]> = {
  location: ['Project_Address', 'project_address', 'location', 'Location', 'project_location', 'Project_Location', 'Project_Display_Address'],
  project_location: ['Project_Address', 'project_address', 'location', 'Location', 'project_location', 'Project_Location', 'Project_Display_Address'],
  project_name: ['project_name', 'Project_Name'],
  lot_number: ['lot_number', 'Lot_Number', 'lot', 'Lot'],
  status: ['status', 'Status', 'lot_status', 'Lot_Status'],
  price: ['price', 'lot_price', 'Lot_Price'],
  total_price: ['total_price', 'total', 'Total', 'Total_Price'],
  car_park_price: ['car_park_price', 'carpark_price', 'Car_Park_Price'],
  storage_price: ['storage_price', 'Storage_Price'],
  internal_area: ['internal_area', 'Internal_Area'],
  external_area: ['external_area', 'External_Area'],
  total_area: ['total_area', 'Total_Area'],
  internal_price_m2: ['internal_price_m2', 'internal_price_M2', 'Internal_Price_M2'],
  storage: ['storage', 'Storage'],
  study: ['study', 'Study'],
  mpr: ['mpr', 'MPR'],
  orientation: ['orientation', 'Orientation'],
  aspect: ['aspect', 'Aspect'],
  level: ['level', 'Level'],
  type: ['type', 'Type'],
  bed: ['bed', 'Bed'],
  bath: ['bath', 'Bath'],
  car: ['car', 'Car'],
};

/**
 * Resolves one field's value against a `property` payload (real or the builder's own preview
 * sample), trying every candidate spelling the backend has been seen to use — whether or not the
 * field has a `prefill` source configured. Shared by the buyer form's `infoValue()` (an `info` field
 * on the live form) and the PDF export (`buildExternalEoiPdfPayload`), so a real link's Property
 * Details always reads the same on both.
 */
export function resolveExternalEoiInfoValue(field: { key: string; prefill?: string }, property: any): string {
  if (!property) return '';
  const aliases = EXTERNAL_EOI_PROPERTY_KEY_ALIASES;
  const source = EXTERNAL_EOI_PROPERTY_SOURCE_MAP[field.prefill];
  // The payload drops the catalogue's `lot_` prefix (`lot_price` → `price`), so the stripped key is
  // both a candidate in its own right and the row to look up in the alias table.
  const stripped = field.key.replace(/^lot_/, '');
  const candidates = [
    ...(source ? [source, ...(aliases[source] || [])] : []),
    field.key,
    stripped,
    ...(aliases[stripped] || []),
    ...(aliases[field.key] || []),
  ];

  for (const key of candidates) {
    const value = property[key];
    if (value !== null && value !== undefined && value !== '') return formatExternalEoiInfoValue(field.key, value);
  }
  return '';
}

/**
 * Formats one answerable field's raw value for display outside the live reactive form — the PDF
 * export has no `FormControl` to read from. Mirrors how the buyer form itself shows each field type
 * (checkbox as its ticked options joined together, file as its name, date as a calendar date),
 * collapsing anything blank to an empty string so the caller can choose its own placeholder.
 */
export function formatExternalEoiFieldAnswer(field: { type: ExternalEoiFieldType }, rawValue: any): string {
  if (rawValue === null || rawValue === undefined) return '';
  if (field.type === 'checkbox') return Array.isArray(rawValue) ? rawValue.join(', ') : '';
  if (field.type === 'file') return (rawValue as ExternalEoiFileValue)?.name || '';
  if (field.type === 'date') {
    const date = new Date(rawValue);
    if (!Number.isNaN(date.getTime())) return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return typeof rawValue === 'string' ? rawValue.trim() : String(rawValue);
}

/** The card uses short labels ("Project", "Lot") where the catalogue carries longer ones ("Project Name", "Lot Number"). An agent's custom label always wins. */
const EXTERNAL_EOI_META_SHORT_LABELS: Record<string, string> = {
  project_name: 'Project',
  lot_number: 'Lot',
  project_location: 'Location',
};

export function externalEoiMetaLabel(field: { key: string; label?: string }): string {
  const catalogueDefault = EXTERNAL_EOI_FIELD_CATALOG.find(c => c.key === field.key)?.label;
  const renamed = !!field.label && field.label !== catalogueDefault;
  return renamed ? field.label! : (EXTERNAL_EOI_META_SHORT_LABELS[field.key] ?? field.label ?? '');
}

/** Icon for a spec tile, taken from the field catalogue so new property fields get one for free. */
export function externalEoiSpecIcon(field: { key: string }): string {
  return EXTERNAL_EOI_FIELD_CATALOG.find(c => c.key === field.key)?.icon || '';
}

/** Section display metadata used to group fields on the buyer form. */
export const EXTERNAL_EOI_SECTIONS: { key: ExternalEoiSection; label: string }[] = [
  { key: 'property', label: 'Property Details' },
  { key: 'buyer', label: 'Your Details' },
  { key: 'financial', label: 'Financial Information' },
  { key: 'sale', label: 'Sale Details' },
  { key: 'dates', label: 'Critical Dates' },
  { key: 'solicitor', label: 'Solicitor Details' },
  { key: 'agency', label: 'Agency Details' },
  { key: 'documents', label: 'Supporting Information' },
  { key: 'other', label: 'Additional Information' },
  { key: 'internal', label: 'Internal / Admin' },
];

/** Fallback heading for a custom section that somehow has no `section_labels` entry. */
export const EXTERNAL_EOI_CUSTOM_SECTION_FALLBACK_LABEL = 'Additional Information';

/**
 * Distinct section keys among `fields`, in the order they first appear (by `field.order`).
 *
 * A custom section (minted via `createExternalEoiCustomSectionKey`) can be dropped anywhere on the
 * form, so section render order can no longer be `EXTERNAL_EOI_SECTIONS`' fixed canonical order —
 * both the builder canvas and the buyer form use this instead, so a custom section renders exactly
 * where the agent placed it rather than always last.
 */
export function resolveExternalEoiSectionOrder(fields: ExternalEoiConfigField[] | null | undefined): string[] {
  const ordered = [...(fields || [])].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  const seen = new Set<string>();
  const out: string[] = [];
  for (const f of ordered) {
    if (f?.section && !seen.has(f.section)) {
      seen.add(f.section);
      out.push(f.section);
    }
  }
  return out;
}

/** One heading's worth of the form body — a run of consecutive same-section fields, in the agent's order. */
export interface ExternalEoiSectionRun {
  section: string;
  fields: ExternalEoiConfigField[];
}

/**
 * The form body as runs of *consecutive* same-section fields, in `order` — NOT one block per
 * section. The builder canvas draws a heading wherever the section changes as you read down the
 * form (see `ExternalEoiComponent.isNewCanvasSection`), so a field dragged below another section's
 * fields shows up under a second copy of its own heading there. The buyer form used to merge every
 * field of a section under one heading regardless of position, so an agent saw "Internal / Admin ·
 * Agency Details · Internal / Admin" in the builder and the buyer got "Internal / Admin · Agency
 * Details" with all four fields pulled into the first — reported as "edit mode and preview are
 * different, it should be the same as edit mode". Both surfaces now derive their grouping here.
 *
 * Property-section fields are excluded: they render in the Property Details card on both surfaces,
 * never as a section of the body.
 */
export function buildExternalEoiSectionRuns(fields: ExternalEoiConfigField[] | null | undefined): ExternalEoiSectionRun[] {
  const ordered = [...(fields || [])].filter(f => !!f && f.section !== 'property').sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const runs: ExternalEoiSectionRun[] = [];
  for (const f of ordered) {
    const current = runs[runs.length - 1];
    if (current && current.section === f.section) current.fields.push(f);
    else runs.push({ section: f.section, fields: [f] });
  }
  return runs;
}

/**
 * Tab labels for the builder's "Available Fields" picker.
 *
 * Deliberately separate from `EXTERNAL_EOI_SECTIONS`: this labels the same
 * `section` keys for the agent-facing catalogue browser, while the buyer
 * form's own section headings (fetched via `EXTERNAL_EOI_SECTIONS`) must stay
 * untouched. Ordered project-fields-first, then client-fields, matching how
 * an agent scans the picker.
 */
export const EXTERNAL_EOI_PICKER_GROUPS: { key: ExternalEoiSection; label: string }[] = [
  { key: 'property', label: 'Project Options' },
  { key: 'buyer', label: 'Client Options' },
  { key: 'sale', label: 'Sale Details' },
  { key: 'financial', label: 'Financial Information' },
  { key: 'dates', label: 'Critical Dates' },
  { key: 'solicitor', label: 'Solicitor Details' },
  { key: 'documents', label: 'Supporting Information' },
  { key: 'agency', label: 'Agency Details' },
  { key: 'other', label: 'Additional Information' },
  { key: 'internal', label: 'Internal / Admin' },
];

/** Prefill source options offered in the Field Settings panel. */
export const EXTERNAL_EOI_PREFILL_OPTIONS: { value: ExternalEoiPrefillSource; label: string }[] = [
  { value: 'none', label: 'No prefill' },
  { value: 'project.name', label: 'Project: Name' },
  { value: 'project.location', label: 'Project: Location' },
  { value: 'lot.number', label: 'Lot: Number' },
  { value: 'lot.price', label: 'Lot: Price' },
  { value: 'lot.bed', label: 'Lot: Bed' },
  { value: 'lot.bath', label: 'Lot: Bath' },
  { value: 'lot.car', label: 'Lot: Car' },
  { value: 'lot.type', label: 'Lot: Type' },
  { value: 'lot.level', label: 'Lot: Level' },
  { value: 'lot.aspect', label: 'Lot: Aspect' },
  { value: 'lot.internal_area', label: 'Lot: Internal Area' },
  { value: 'lot.external_area', label: 'Lot: External Area' },
  { value: 'lot.total_area', label: 'Lot: Total Area' },
  { value: 'lot.internal_price_m2', label: 'Lot: Internal Price M2' },
  { value: 'lot.storage', label: 'Lot: Storage' },
  { value: 'lot.study', label: 'Lot: Study' },
  { value: 'lot.mpr', label: 'Lot: MPR' },
  { value: 'lot.orientation', label: 'Lot: Orientation' },
  { value: 'lot.car_park_price', label: 'Lot: Car Park Price' },
  { value: 'lot.storage_price', label: 'Lot: Storage Price' },
  { value: 'lot.total_price', label: 'Lot: Total Price' },
  { value: 'lot.status', label: 'Lot: Status' },
  { value: 'contact.full_name', label: 'Contact: Full Name' },
  { value: 'contact.first_name', label: 'Contact: First Name' },
  { value: 'contact.last_name', label: 'Contact: Last Name' },
  { value: 'contact.email', label: 'Contact: Email' },
  { value: 'contact.mobile', label: 'Contact: Mobile' },
  { value: 'contact.phone', label: 'Contact: Phone' },
  { value: 'contact.address', label: 'Contact: Address' },
  { value: 'agent.name', label: 'Agent: Name' },
  { value: 'agent.email', label: 'Agent: Email' },
];

/**
 * Build a default configuration field from a catalogue entry, applying the
 * sensible defaults shown in the design (property info = read-only, key buyer
 * fields = required).
 */
export function catalogToConfigField(cat: ExternalEoiCatalogField, order: number): ExternalEoiConfigField {
  return {
    key: cat.key,
    label: cat.label,
    type: cat.type,
    section: cat.section,
    eoiField: cat.eoiField,
    required: !!cat.recommendedRequired,
    readOnly: !!cat.readOnlyByDefault,
    allowEditing: !cat.readOnlyByDefault,
    prefill: cat.defaultPrefill ?? 'none',
    helpText: cat.helpTextByDefault ?? '',
    placeholder: '',
    options: cat.options ? [...cat.options] : undefined,
    fileConfig: cat.fileConfig ? { ...cat.fileConfig } : undefined,
    order,
  };
}

/**
 * Builds a new "free text" block — not catalogue-sourced (there's no fixed field behind it, so
 * `catalogToConfigField` doesn't apply), and there can be any number of them on one form, so each
 * gets a freshly-minted unique key rather than a fixed catalogue one.
 */
export function createExternalEoiStaticTextField(section: string, order: number): ExternalEoiConfigField {
  return {
    key: `static_text_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    label: 'Text block',
    type: 'static_text',
    section,
    required: false,
    readOnly: false,
    allowEditing: true,
    prefill: 'none',
    content: '',
    order,
  };
}

/** Field types offered as a "custom field" — every real input type minus the internal-only ones. */
export const EXTERNAL_EOI_CUSTOM_FIELD_TYPES: { value: ExternalEoiFieldType; label: string; icon: string }[] = [
  { value: 'text', label: 'Short Text', icon: 'fa-solid fa-font' },
  { value: 'textarea', label: 'Long Text', icon: 'fa-solid fa-align-left' },
  { value: 'email', label: 'Email', icon: 'fa-solid fa-envelope' },
  { value: 'tel', label: 'Phone', icon: 'fa-solid fa-phone' },
  { value: 'select', label: 'Dropdown', icon: 'fa-solid fa-caret-down' },
  { value: 'checkbox', label: 'Checkbox', icon: 'fa-regular fa-square-check' },
  { value: 'radio', label: 'Radio Button', icon: 'fa-regular fa-circle-dot' },
  { value: 'date', label: 'Date Picker', icon: 'fa-solid fa-calendar-days' },
  { value: 'file', label: 'File Upload', icon: 'fa-solid fa-paperclip' },
];

/**
 * Builds a totally custom, ad hoc field — not from the catalogue, no `eoiField` mapping, so its
 * answer is only ever captured in a submission's `values` (never `eoi_values`; see
 * `buildExternalEoiSubmission` and `isCustom` on `ExternalEoiConfigField`). For an agent question
 * that has no corresponding EOI/lead column.
 */
export function createExternalEoiCustomField(type: ExternalEoiFieldType, section: string, order: number): ExternalEoiConfigField {
  return {
    key: `custom_${type}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    label: 'New Question',
    type,
    section,
    required: false,
    readOnly: false,
    allowEditing: true,
    prefill: 'none',
    isCustom: true,
    options: ['select', 'checkbox', 'radio'].includes(type) ? ['Option 1', 'Option 2'] : undefined,
    order,
  };
}

/**
 * `eoiField` values seen NOT to land on any real `EOI_form` control when checked against
 * `edit-eoi.component.ts` — see
 * `external-eoi-submission-eoi-field-mapping-backend-requirements.md`. A custom field could
 * otherwise be pointed at one of these through `EXTERNAL_EOI_CRM_LINK_OPTIONS` below and look
 * linked while the answer actually goes nowhere, so they're excluded from that list until backend
 * confirms where (if anywhere) they're meant to land.
 */
const EXTERNAL_EOI_UNCONFIRMED_EOI_FIELDS = new Set([
  'email',
  'mobile_no',
  'phone_no',
  'address',
  'address_line_1',
  'address_line_2',
  'suburb',
  'state',
  'postcode',
  'country',
  'date_of_birth',
  'occupation',
  'employer',
  'source_of_funds',
  'income_range',
  'buyers_id',
  'upload_other_documents',
  'signed_reservation_agreementform',
  'deposit_receipt',
  'referral_source',
]);

/**
 * Where a custom field's answer can be linked to write back to a real EOI column — offered in
 * Field Settings for any field with `isCustom: true` (see `ext-field` "Link to CRM Field" in
 * `external-eoi.component.html`). Derived from the catalogue's own `eoiField` mappings rather than
 * a separate hand-maintained list, so it can never drift from what catalogue fields already write
 * to — but only the confirmed-working ones (see `EXTERNAL_EOI_UNCONFIRMED_EOI_FIELDS` above), and
 * never an `info`-type target (a read-only project/lot identity column, not something a buyer's
 * custom answer should silently overwrite).
 */
export const EXTERNAL_EOI_CRM_LINK_OPTIONS: { value: string; label: string }[] = (() => {
  const seen = new Set<string>();
  const options: { value: string; label: string }[] = [];
  for (const c of EXTERNAL_EOI_FIELD_CATALOG) {
    if (!c.eoiField || c.type === 'info' || EXTERNAL_EOI_UNCONFIRMED_EOI_FIELDS.has(c.eoiField) || seen.has(c.eoiField)) continue;
    seen.add(c.eoiField);
    options.push({ value: c.eoiField, label: c.label });
  }
  return options.sort((a, b) => a.label.localeCompare(b.label));
})();

/** A plain visual rule — no answer, no label shown on the buyer form. Also used as the placeholder member of an otherwise-empty custom section, so an abandoned section renders nothing rather than a bare heading. */
export function createExternalEoiDividerField(section: string, order: number): ExternalEoiConfigField {
  return {
    key: `divider_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    label: 'Divider',
    type: 'divider',
    section,
    required: false,
    readOnly: false,
    allowEditing: true,
    prefill: 'none',
    order,
  };
}

/**
 * Builds a "Columns" layout container — a real `fields` entry (see `columns`/`columnCount` on
 * `ExternalEoiConfigField`) whose slots start empty; fields are moved into them afterwards by the
 * builder's drag-and-drop, not by this constructor.
 */
export function createExternalEoiColumnsField(section: string, order: number, columnCount = 2): ExternalEoiConfigField {
  return {
    key: `columns_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    label: 'Columns',
    type: 'columns',
    section,
    required: false,
    readOnly: false,
    allowEditing: true,
    prefill: 'none',
    columns: Array.from({ length: columnCount }, () => []),
    columnCount,
    order,
  };
}

/** Mints a fresh key for a new custom section — paired with a `section_labels` entry for its heading. */
export function createExternalEoiCustomSectionKey(): string {
  return `custom_section_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Section key for the default template's "Deposit Authority" block — a fixed custom-section key
 * rather than a freshly-minted `createExternalEoiCustomSectionKey()`, so re-applying the template on
 * a project that already carries it doesn't leave a second, orphaned heading in `section_labels`.
 */
export const EXTERNAL_EOI_DEPOSIT_AUTHORITY_SECTION_KEY = 'custom_section_deposit_authority';

/** The Deposit Authority wording the default template seeds — an agent can rewrite it like any free-text block. */
export const EXTERNAL_EOI_DEPOSIT_AUTHORITY_TEXT = [
  'I/We submit this Expression of Interest for the property described above and authorise the agent to hold the initial deposit in its trust account pending the preparation and execution of a Contract of Sale.',
  'I/We acknowledge that this Expression of Interest is not a contract, does not reserve or secure the property, and that the property remains available for sale until a Contract of Sale has been signed by both parties. The initial deposit is fully refundable if a Contract of Sale is not entered into.',
  'I/We confirm that the details provided are true and correct and consent to them being used to prepare the Contract of Sale.',
].join('\n\n');

/**
 * The layout a never-configured project starts from — the client's "EOI & Deposit Authority
 * (Retail)" form, section by section, built from catalogue fields plus a Deposit Authority block
 * (free text + a required acknowledgement checkbox). Also applied on demand from Form Settings →
 * "Reset to default template". Every section it adds is removable in the builder.
 *
 * Section order and field choices follow the real "EOI & Deposit Authority Fillable - RETAIL
 * 2025.pdf" (Queen's Wharf Tower) page by page. Credit card number/CVV/expiry are deliberately NOT
 * captured here — a digital form storing raw card details is a PCI-DSS problem the paper form
 * (handled manually by Avanti/HWL Ebsworth) doesn't have in the same way; see
 * `ExternalEoiPdfService`, which renders that box on the PDF as a blank fillable field instead.
 * Fixed keys throughout (no `Date.now()` minting) so applying the template twice produces the same
 * fields, not duplicates.
 */
export function buildExternalEoiDefaultTemplate(): { fields: ExternalEoiConfigField[]; sectionLabels: Record<string, string> } {
  const fields: ExternalEoiConfigField[] = [];
  const add = (key: string, overrides: Partial<ExternalEoiConfigField> = {}): void => {
    const cat = EXTERNAL_EOI_FIELD_CATALOG.find(c => c.key === key);
    if (!cat) return;
    fields.push({ ...catalogToConfigField(cat, fields.length), ...overrides, order: fields.length });
  };

  // 1. Property Details — the identity block plus the figures on the paper form's header.
  for (const key of [
    'project_name',
    'lot_number',
    'lot_level',
    'lot_type',
    'lot_bed',
    'lot_bath',
    'lot_car',
    'lot_price',
    'lot_car_park_price',
    'lot_storage_price',
    'lot_total_price',
  ]) {
    add(key);
  }

  // 2. Your Details — Buyer #1 in full, Buyer #2 in full, then the shared address/company/trust and
  //    purchase-purpose/FIRB/colour-scheme questions that apply to the purchase as a whole.
  for (const key of [
    'full_name',
    'email',
    'phone_mobile',
    'date_of_birth',
    'purchaser_2',
    'email_purchaser_2',
    'phone_purchaser_2',
    'date_of_birth_purchaser_2',
    'current_address',
    'company_name',
    'company_acn',
    'trust_name',
    'buyer_purchasing_for',
    'firb',
    'firb_nationality',
    'colour_scheme',
  ]) {
    add(key);
  }

  // 3. Optional Upgrades (Inc GST)
  add('upgrade_finishes', { section: 'sale' });
  add('upgrade_appliances', { section: 'sale' });
  add('upgrade_lighting_automation', { section: 'sale' });
  add('upgrade_extra', { section: 'sale' });

  // 4. Price / Deposit
  add('purchase_type');
  add('source_of_funds', { section: 'financial' });
  add('upgrade_price_total', { section: 'financial' });
  add('initial_deposit_choice', { section: 'financial' });
  add('balance_of_deposit', { section: 'financial' });
  add('total_deposit', { section: 'financial' });
  add('bal_deposit_due_date', { section: 'financial' });
  add('finance_due_date', { section: 'financial' });
  add('sales_related_notes', { label: 'Notes', section: 'financial' });

  // 5. Solicitor's Information
  add('solicitor_firm');
  add('solicitor_name', { label: 'Contact Person' });
  add('solicitor_phone');
  add('solicitor_email');
  add('solicitor_address');

  // 6. Identification of Buyer / Salesperson
  add('identification_sighted');
  add('agent', { label: 'Salesperson', prefill: 'agent.name' });

  // 7. Deposit Authority — the authority wording and the buyer's acknowledgement of it. The credit
  //    card / bank transfer particulars themselves are fixed print-only boxes on the PDF, not
  //    captured fields — see this function's doc comment.
  fields.push({
    key: 'static_text_deposit_authority',
    label: 'Deposit authority wording',
    type: 'static_text',
    section: EXTERNAL_EOI_DEPOSIT_AUTHORITY_SECTION_KEY,
    required: false,
    readOnly: false,
    allowEditing: true,
    prefill: 'none',
    content: EXTERNAL_EOI_DEPOSIT_AUTHORITY_TEXT,
    width: 'full',
    order: fields.length,
  });
  fields.push({
    key: 'custom_checkbox_deposit_authority_ack',
    label: 'Acknowledgement',
    type: 'checkbox',
    section: EXTERNAL_EOI_DEPOSIT_AUTHORITY_SECTION_KEY,
    required: true,
    readOnly: false,
    allowEditing: true,
    prefill: 'none',
    isCustom: true,
    options: ['I/We have read and agree to the above'],
    width: 'full',
    order: fields.length,
  });

  // 8. Supporting Information — ID plus the deposit slip; the other uploads stay in the catalogue.
  add('upload_identification');
  add('deposit_receipt');
  add('additional_comments');

  // 9. Agency Details
  add('agency');

  return {
    fields,
    sectionLabels: {
      financial: 'Price & Deposit',
      [EXTERNAL_EOI_DEPOSIT_AUTHORITY_SECTION_KEY]: 'Deposit Authority',
    },
  };
}

// ---------------------------------------------------------------------------
// PDF export — see ExternalEoiPdfService
// ---------------------------------------------------------------------------

/** One buyer's identity block, as laid out on the paper form (Buyer #1 / Buyer #2). */
export interface ExternalEoiRetailPdfBuyer {
  /**
   * The paper form splits a name into "First & Middle Names" and "Surname" boxes; the EOI model
   * only ever captures one combined name field (`full_name` / `purchaser_2`), so the whole name is
   * placed here and `surname` is always blank rather than guessing where one name ends and the
   * other begins.
   */
  firstMiddleNames: string;
  surname: string;
  email: string;
  phone: string;
  dateOfBirth: string;
}

/**
 * Every value `ExternalEoiPdfService` needs to fill in the exact "EOI & Deposit Authority
 * (Retail)" layout — one field per box on the paper form, already formatted for display. Plain
 * data, no Angular/jsPDF dependency, so the renderer can't drift from what was actually answered.
 *
 * Deliberately excludes card number/CVV/expiry and both signatures: the first is a PCI-DSS risk to
 * digitise and store, the second can't be captured by a typed answer — both render as blank boxes
 * on the PDF instead (see `ExternalEoiPdfService`).
 */
export interface ExternalEoiRetailPdfData {
  /** The masthead, footer and bank-transfer reference name the property/lot itself — everything else on the page (the seller entity, Avanti/HWL Ebsworth wording) is fixed regardless of project. */
  projectName: string;
  apartmentNumber: string;
  buyer1: ExternalEoiRetailPdfBuyer;
  buyer2: ExternalEoiRetailPdfBuyer;
  /**
   * The paper form only ever printed two buyer blocks; this one renders an extra "Buyer #3" section
   * (same fields as #1/#2) only when the EOI actually has a third purchaser — `purchaser_3` is the
   * catalogue's ceiling (there is no `purchaser_4` anywhere in this codebase), so a fourth buyer has
   * nowhere to come from without a new catalogue field and backend column.
   */
  buyer3: ExternalEoiRetailPdfBuyer;
  fullAddress: string;
  companyName: string;
  companyAcn: string;
  trustName: string;
  purchasingFor: string;
  firb: string;
  firbNationality: string;
  colourScheme: string;
  upgrades: {
    finishes: string[];
    appliances: string[];
    lightingAutomation: string[];
    extra: string[];
  };
  propertyPrice: string;
  upgradePrice: string;
  totalPrice: string;
  initialDepositChoice: string;
  balanceDeposit: string;
  totalDeposit: string;
  notes: string;
  solicitor: {
    firmName: string;
    contactPerson: string;
    phone: string;
    email: string;
    address: string;
  };
  identificationSighted: string;
  salesperson: string;
  depositAuthority: {
    apartmentNumber: string;
    depositAmount: string;
    fullName: string;
    address: string;
    phone: string;
    email: string;
  };
}

/** `9000000` → `$9,000,000`; passes non-numeric input through unchanged rather than swallowing it. */
function formatExternalEoiRetailMoney(raw: string): string {
  if (!raw) return '';
  const num = Number(String(raw).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(num) ? num.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }) : raw;
}

/**
 * Builds the retail EOI PDF's field values from whatever answers exist so far (CRM prefill, an
 * agent's saved draft, or the buyer's own in-progress typing) plus the linked property record for
 * the read-only lot/price figures. `fields` is the project's own placed configuration — needed
 * because a box's value is looked up by which field maps to the matching EOI/CRM column
 * (`findPlacedField`), not by assuming the project kept the catalogue's own key for it. `projectName`
 * is the one piece of branding that does vary by project — everything else on the page (the seller
 * entity, Avanti/HWL Ebsworth wording) is fixed regardless of it; falls back to the property record's
 * own `project_name` when not passed explicitly. One builder shared by every "Download PDF" entry
 * point (a live form in the View Link overlay, or a saved link fetched by its own view endpoint).
 */
export function buildExternalEoiRetailPdfData(input: {
  fields: ExternalEoiConfigField[];
  property?: any;
  projectName?: string;
  values: Record<string, any>;
}): ExternalEoiRetailPdfData {
  const { property, values } = input;
  const flatFields = flattenExternalEoiAnswerableFields(input.fields);

  /**
   * Finds whichever field the project actually placed for a given catalogue concept — matched by
   * its resolved EOI/CRM column (`resolveExternalEoiField`), not by assuming the placed field kept
   * the catalogue's own key. A field's *label* is freely renamed per project (an agent can relabel
   * the catalogue's "Full Name" field to "First & Middle Names" to match this exact paper form), but
   * its key never changes when only the label does, so an exact-key match still covers that common
   * case; a *custom* field carries its own generated key and only ever resolves here if the agent
   * explicitly linked it to the same CRM column via "Link to CRM Field". Reported live: a project's
   * on-screen "First & Middle Names" box showed the buyer's name, but the exported PDF's matching box
   * came out blank — this is why, and matching by EOI column instead of by literal key fixes it.
   */
  const findPlacedField = (catalogueKey: string): ExternalEoiConfigField | undefined => {
    const exact = flatFields.find(f => f.key === catalogueKey);
    if (exact) return exact;
    const targetEoiField = EXTERNAL_EOI_FIELD_CATALOG.find(c => c.key === catalogueKey)?.eoiField;
    return targetEoiField ? flatFields.find(f => resolveExternalEoiField(f) === targetEoiField) : undefined;
  };

  const answer = (catalogueKey: string): string => {
    const placed = findPlacedField(catalogueKey);
    const typeSource = placed || EXTERNAL_EOI_FIELD_CATALOG.find(c => c.key === catalogueKey);
    if (!typeSource) return '';
    return formatExternalEoiFieldAnswer(typeSource, values?.[placed?.key ?? catalogueKey]);
  };
  const info = (catalogueKey: string): string => {
    const placed = findPlacedField(catalogueKey);
    const cat = EXTERNAL_EOI_FIELD_CATALOG.find(c => c.key === catalogueKey);
    const source = placed || (cat ? { key: cat.key, prefill: cat.defaultPrefill } : undefined);
    return source ? resolveExternalEoiInfoValue(source, property) : '';
  };
  const checked = (catalogueKey: string): string[] => {
    const placed = findPlacedField(catalogueKey);
    const raw = values?.[placed?.key ?? catalogueKey];
    return Array.isArray(raw) ? raw : [];
  };
  /**
   * Some paper-form concepts are backed by more than one catalogue field in this codebase — a
   * buyer's name is captured either as `full_name` (the linked contact's name) or as `purchaser_1`
   * (the EOI's own Purchaser Details, filled in independently of any contact — confirmed live: a
   * project with `purchaser_1`/`purchaser_2` answered on the EOI record still came out blank here
   * because only `full_name` was ever tried). Tries each in order, first non-blank wins.
   */
  const answerFromCandidates = (catalogueKeys: string[]): string => {
    for (const key of catalogueKeys) {
      const value = answer(key);
      if (value) return value;
    }
    return '';
  };
  /** Same as `answerFromCandidates`, formatted as money — for boxes with more than one possible dollar-figure source. */
  const moneyFromCandidates = (catalogueKeys: string[]): string => {
    const raw = answerFromCandidates(catalogueKeys);
    return raw ? formatExternalEoiRetailMoney(raw) : '';
  };

  const buyer1Name = answerFromCandidates(['full_name', 'purchaser_1']);
  const buyer1Phone = answerFromCandidates(['phone_mobile', 'phone_home']);
  const composedAddress = [answer('address_line_1'), answer('address_line_2'), answer('suburb'), answer('state'), answer('postcode')]
    .filter(Boolean)
    .join(', ');
  const fullAddress = answer('current_address') || composedAddress;

  // "Buyer Purchasing For" has no equivalent field on older projects; `purchase_type` (Sale Details)
  // asks the same question with a third option ("First Home Buyer") this paper form's two checkboxes
  // don't have room for, so it's folded into "Investment Purposes" rather than dropped.
  const purchaseType = answer('purchase_type');
  const purchasingFor =
    answer('buyer_purchasing_for') || (purchaseType ? (purchaseType === 'Owner Occupier' ? 'Owner Occupier' : 'Investment Purposes') : '');

  // "Initial Deposit" is normally its own $2,000/$5,000 choice field; an older project that only ever
  // captured a raw `deposit_amount` still lets the matching checkbox be inferred from it.
  const depositAmountField = findPlacedField('deposit_amount');
  const rawDepositAmount = depositAmountField ? values?.[depositAmountField.key] : undefined;
  const depositAmountNumber = rawDepositAmount != null ? Number(String(rawDepositAmount).replace(/[^0-9.]/g, '')) : NaN;
  const initialDepositChoice =
    answer('initial_deposit_choice') ||
    (Number.isFinite(depositAmountNumber) && Math.round(depositAmountNumber) === 2000
      ? '$2,000'
      : Number.isFinite(depositAmountNumber) && Math.round(depositAmountNumber) === 5000
        ? '$5,000'
        : '');

  return {
    projectName: input.projectName || info('project_name') || property?.project_name || '',
    apartmentNumber: info('lot_number'),
    buyer1: {
      firstMiddleNames: buyer1Name,
      surname: '',
      email: answer('email'),
      phone: buyer1Phone,
      dateOfBirth: answer('date_of_birth'),
    },
    buyer2: {
      firstMiddleNames: answer('purchaser_2'),
      surname: '',
      email: answer('email_purchaser_2'),
      phone: answer('phone_purchaser_2'),
      dateOfBirth: answer('date_of_birth_purchaser_2'),
    },
    // No `email_purchaser_3`/`phone_purchaser_3`/`date_of_birth_purchaser_3` catalogue fields exist —
    // Purchaser 3 has never captured more than a name and nationality anywhere in this codebase.
    buyer3: {
      firstMiddleNames: answer('purchaser_3'),
      surname: '',
      email: '',
      phone: '',
      dateOfBirth: '',
    },
    fullAddress,
    companyName: answer('company_name'),
    companyAcn: answer('company_acn'),
    trustName: answer('trust_name'),
    purchasingFor,
    firb: answer('firb'),
    firbNationality: answer('firb_nationality'),
    colourScheme: answer('colour_scheme'),
    upgrades: {
      finishes: checked('upgrade_finishes'),
      appliances: checked('upgrade_appliances'),
      lightingAutomation: checked('upgrade_lighting_automation'),
      extra: checked('upgrade_extra'),
    },
    // The listing's advertised price (`lot_price`/`lot_total_price`) wins when the project has it on
    // the form; a project that instead tracks the EOI's own negotiated `contract_price`/`subtotal`
    // (Sale Details) falls back to those — confirmed live as the same figure in both boxes when there
    // are no separate upgrades.
    propertyPrice: info('lot_price') || moneyFromCandidates(['contract_price', 'subtotal']),
    upgradePrice: moneyFromCandidates(['upgrade_price_total', 'upgrades_total']),
    totalPrice: info('lot_total_price') || moneyFromCandidates(['contract_price', 'subtotal']),
    initialDepositChoice,
    balanceDeposit: formatExternalEoiRetailMoney(answer('balance_of_deposit')),
    totalDeposit: formatExternalEoiRetailMoney(answer('total_deposit')),
    // Deliberately not widened to `admin_only_comments`/`commission_comments` — both are explicitly
    // internal-facing by name, and this PDF is meant for the buyer.
    notes: answerFromCandidates(['sales_related_notes', 'deposit_comments']),
    solicitor: {
      firmName: answer('solicitor_firm'),
      contactPerson: answer('solicitor_name'),
      phone: answer('solicitor_phone'),
      email: answer('solicitor_email'),
      address: answer('solicitor_address'),
    },
    identificationSighted: answer('identification_sighted'),
    salesperson: answer('agent'),
    depositAuthority: {
      apartmentNumber: info('lot_number'),
      depositAmount: formatExternalEoiRetailMoney(answer('deposit_amount')) || initialDepositChoice,
      fullName: buyer1Name,
      address: fullAddress,
      phone: buyer1Phone,
      email: answer('email'),
    },
  };
}

/**
 * True when the PDF's identity/price boxes are all blank — not because none of this data exists
 * anywhere on the EOI, but because the project's own External EOI configuration doesn't currently
 * have a field *placed* for any of them (see `buildExternalEoiRetailPdfData`'s `fields` param and
 * `findPlacedField` — a field with nothing placed for its EOI column has nothing to read, no matter
 * how many alternate keys are tried). Callers use this to warn the agent with something actionable
 * ("add these fields, or Reset to default template") instead of a silently blank download that reads
 * as a bug to keep chasing.
 */
export function isExternalEoiRetailPdfDataMostlyEmpty(data: ExternalEoiRetailPdfData): boolean {
  return !(
    data.buyer1.firstMiddleNames ||
    data.buyer2.firstMiddleNames ||
    data.buyer3.firstMiddleNames ||
    data.companyName ||
    data.propertyPrice ||
    data.totalPrice ||
    data.solicitor.firmName ||
    data.salesperson
  );
}
